import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  Paper,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { api } from "../apiClient";
import { APP_FOOTER_TEXT, APP_LOGIN_LOGO_TEXT } from "../config";
import { buildAppTheme } from "../hooks/useAppTheme";
import { brandReady } from "../brandAssets";
import BrandLogo from "./BrandLogo";
import { t } from "../i18n";
import { fieldSx, primaryButtonSx, uiMotion, uiPalette, uiRadii } from "../uiStyles";

function Login({ onLogin, invitationToken = "" }) {
  const getSystemTheme = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  const controls = useAnimation();
  const [mode, setMode] = useState("login");
  const [loginTheme, setLoginTheme] = useState(() => {
    if (typeof window === "undefined") return getSystemTheme();
    return (
      window.__TRACKER_LOGIN_THEME__ ||
      localStorage.getItem("tracker_login_theme") ||
      getSystemTheme()
    );
  });
  const [loginLocale, setLoginLocale] = useState(() => {
    if (typeof window === "undefined") return "ru";
    return (
      window.__TRACKER_LOGIN_LOCALE__ ||
      localStorage.getItem("tracker_login_locale") ||
      "ru"
    );
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [allowRememberMe, setAllowRememberMe] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [invitationEmail, setInvitationEmail] = useState("");
  const [invitationValid, setInvitationValid] = useState(!invitationToken);
  const [showError, setShowError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpSending, setOtpSending] = useState(false);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpChecking, setOtpChecking] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const usernameRef = useRef(null);
  const otpRefs = useRef([]);

  useEffect(() => {
    let mounted = true;
    const reveal = async () => {
      await brandReady();
      if (!mounted) return;
      controls.start({
        scale: 1,
        opacity: 1,
        transition: { duration: 0.8, ease: "easeOut" },
      });
    };
    reveal();
    return () => {
      mounted = false;
    };
  }, [controls]);

  useEffect(() => {
    let mounted = true;
    api
      .get("/login/settings")
      .then((response) => {
        if (!mounted) return;
        const next = response.data?.settings || {};
        const nextTheme =
          next.theme === "dark"
            ? "dark"
            : next.theme === "system"
              ? "system"
              : "light";
        const nextLocale = next.locale === "en" ? "en" : "ru";
        setLoginTheme(nextTheme);
        setLoginLocale(nextLocale);
        if (typeof window !== "undefined") {
          window.__TRACKER_LOGIN_THEME__ = nextTheme;
          window.__TRACKER_LOGIN_LOCALE__ = nextLocale;
          localStorage.setItem("tracker_login_theme", nextTheme);
          localStorage.setItem("tracker_login_locale", nextLocale);
        }
        setAllowRememberMe(!!next.allowRememberMe);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!invitationToken) return;
    setMode("invitation");
    setLoading(true);
    api
      .get(`/invite/${encodeURIComponent(invitationToken)}`)
      .then((response) => {
        setInvitationEmail(response.data?.email || "");
        setInvitationValid(true);
        setShowError("");
      })
      .catch((error) => {
        setInvitationValid(false);
        setShowError(
          error.response?.data?.error ||
            "Ссылка-приглашение недействительна или устарела",
        );
      })
      .finally(() => setLoading(false));
  }, [invitationToken]);

  const showErr = (message) => {
    setShowError(message);
    setTimeout(() => setShowError(""), 6000);
  };

  const activeLocale = loginLocale || "ru";
  const resolvedLoginTheme =
    loginTheme === "system" ? getSystemTheme() : loginTheme;
  const isDark = resolvedLoginTheme === "dark";
  const palette = isDark ? uiPalette.dark : uiPalette.light;
  const appTheme = useMemo(() => buildAppTheme(isDark ? "dark" : "light"), [isDark]);
  const [, setTimerTick] = useState(0);

  useEffect(() => {
    if (!resendAvailableAt) return undefined;
    const id = setInterval(() => setTimerTick((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [resendAvailableAt]);

  const fieldStyle = fieldSx(isDark);

  const buttonStyle = {
    ...primaryButtonSx,
    width: "100%",
    mt: 1.5,
  };

  const linkStyle = {
    mt: 2.15,
    color: "text.secondary",
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: "0.01em",
    textDecoration: "none",
    borderBottom: "1px solid transparent",
    transition: "color 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      color: "text.primary",
      borderColor: "ui.borderStrong",
    },
  };

  const focusOtp = (index) =>
    setTimeout(() => otpRefs.current[index]?.focus(), 0);
  const blurOtp = () => otpRefs.current.forEach((node) => node?.blur?.());

  const resetAllFields = () => {
    setUsername("");
    setPassword("");
    setRememberMe(false);
    setResetEmail("");
    setResetCode("");
    setNewPass("");
    setNewPass2("");
    setVerificationToken("");
    setOtpDigits(["", "", "", "", "", ""]);
    setOtpSending(false);
    setOtpSuccess(false);
    setOtpError(false);
    setOtpChecking(false);
    setLoading(false);
    setShowError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/login", {
        username,
        password,
        rememberMe,
      });
      if (response.data.requiresTwoFactor && response.data.verificationToken) {
        setVerificationToken(response.data.verificationToken);
        setMode("two_factor");
        setOtpDigits(["", "", "", "", "", ""]);
        setOtpSending(false);
        setOtpSuccess(false);
        setOtpError(false);
        setOtpChecking(false);
        setResendAvailableAt(Date.now() + 5 * 60 * 1000);
        setShowError("");
        focusOtp(0);
        return;
      }
      if (response.data.token) {
        setAllowRememberMe(!!response.data.settings?.allowRememberMe);
        onLogin({
          token: response.data.token,
          settings: response.data.settings,
          isAdmin: response.data.isAdmin,
          email: username.toLowerCase(),
          rememberMe: !!response.data.rememberMe,
          avatarUrl: response.data.settings?.avatarUrl || "",
        });
      } else {
        throw new Error("Missing token");
      }
    } catch (error) {
      controls.start({
        x: [0, -10, 10, -10, 10, 0],
        transition: { duration: 0.4 },
      });
      showErr(
        error.response?.data?.error || t("login.invalidPassword", activeLocale),
      );
      setUsername("");
      setPassword("");
      setRememberMe(false);
      setTimeout(() => usernameRef.current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    setLoading(true);
    try {
      const response = await api.post("/reset/send_code", {
        email: resetEmail,
      });
      if (response.data.ok) {
        setMode("reset_code");
        setShowError("");
      } else {
        showErr(t("login.emailNotFound"));
        setResetEmail("");
      }
    } catch (error) {
      showErr(error.response?.data?.error || t("login.emailNotFound"));
      setResetEmail("");
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (newPass !== newPass2) {
      showErr(t("login.passwordsMismatch"));
      setNewPass("");
      setNewPass2("");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/reset/confirm", {
        email: resetEmail,
        code: resetCode,
        new_password: newPass,
      });
      if (response.data.ok) {
        setMode("login");
        setResetEmail("");
        setResetCode("");
        setNewPass("");
        setNewPass2("");
        setLoading(false);
        setTimeout(() => {
          setSuccessMsg(t("login.passwordChanged"));
          setTimeout(() => setSuccessMsg(""), 3000);
        }, 200);
      } else {
        showErr(t("login.invalidResetCode"));
        setResetCode("");
        setNewPass("");
        setNewPass2("");
      }
    } catch (error) {
      showErr(error.response?.data?.error || t("login.invalidResetCode"));
      setResetCode("");
      setNewPass("");
      setNewPass2("");
    }

    setLoading(false);
  };

  const handleAcceptInvitation = async () => {
    if (newPass !== newPass2) {
      showErr(t("login.passwordsMismatch", activeLocale));
      return;
    }
    setLoading(true);
    try {
      await api.post(`/invite/${encodeURIComponent(invitationToken)}/accept`, {
        password: newPass,
      });
      setUsername(invitationEmail);
      setPassword("");
      setNewPass("");
      setNewPass2("");
      setMode("login");
      setSuccessMsg(
        activeLocale === "en"
          ? "Password set. You can now sign in"
          : "Пароль задан. Теперь можно войти",
      );
    } catch (error) {
      showErr(
        error.response?.data?.error ||
          (activeLocale === "en"
            ? "Could not accept invitation"
            : "Не удалось принять приглашение"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!verificationToken || Date.now() < resendAvailableAt) return;
    setOtpSending(true);
    try {
      await api.post("/login/resend", { verificationToken });
      setResendAvailableAt(Date.now() + 5 * 60 * 1000);
      setSuccessMsg(t("login.codeSent", activeLocale));
      setTimeout(() => setSuccessMsg(""), 3000);
      setOtpDigits(["", "", "", "", "", ""]);
      setOtpError(false);
      setOtpSuccess(false);
      setOtpChecking(false);
      focusOtp(0);
    } catch (error) {
      showErr(
        error.response?.data?.error || t("login.invalidPassword", activeLocale),
      );
    } finally {
      setOtpSending(false);
    }
  };

  const handleBackToLogin = () => {
    blurOtp();
    resetAllFields();
    setMode("login");
  };

  const verifyOtpDigits = async (next) => {
    blurOtp();
    setLoading(true);
    setOtpChecking(true);
    setOtpSuccess(false);
    setOtpError(false);
    const startedAt = Date.now();
    try {
      const response = await api.post("/login/verify", {
        verificationToken,
        code: next.join(""),
        rememberMe,
      });
      const waitMs = Math.max(0, 1000 - (Date.now() - startedAt));
      if (waitMs) await new Promise((resolve) => setTimeout(resolve, waitMs));
      if (response.data.token) {
        setOtpSuccess(true);
        await controls.start({
          scale: [1, 1.018, 0.992, 1],
          opacity: [1, 1, 0.96, 1],
          transition: { duration: 0.55, ease: "easeInOut" },
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        onLogin({
          token: response.data.token,
          settings: response.data.settings,
          isAdmin: response.data.isAdmin,
          email: response.data.settings?.email || username.toLowerCase(),
          rememberMe: !!response.data.rememberMe,
          avatarUrl: response.data.settings?.avatarUrl || "",
        });
      }
    } catch (error) {
      const waitMs = Math.max(0, 1000 - (Date.now() - startedAt));
      if (waitMs) await new Promise((resolve) => setTimeout(resolve, waitMs));
      setOtpError(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await controls.start({
        x: [0, -10, 10, -10, 10, 0],
        transition: { duration: 0.4 },
      });
      showErr(
        error.response?.data?.error || t("login.invalidPassword", activeLocale),
      );
      setOtpDigits(["", "", "", "", "", ""]);
      setOtpError(false);
    } finally {
      setOtpChecking(false);
      setLoading(false);
      if (mode === "two_factor") {
        setTimeout(() => focusOtp(0), 30);
      }
    }
  };

  const handleOtpChange = async (index, rawValue) => {
    if (otpChecking) return;
    const value = (rawValue || "").replace(/\D/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) {
      focusOtp(index + 1);
    }
    if (next.every(Boolean)) {
      await verifyOtpDigits(next);
    }
  };

  const handleOtpPaste = async (event) => {
    if (otpChecking) {
      event.preventDefault();
      return;
    }
    const pasted = (event.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let index = 0; index < pasted.length; index += 1) {
      next[index] = pasted[index];
      setOtpDigits([...next]);
      focusOtp(Math.min(index + 1, 5));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 55));
    }
    if (pasted.length < 6) {
      focusOtp(Math.min(pasted.length, 5));
      return;
    }
    await verifyOtpDigits(next);
  };

  const handleOtpKeyDown = (index, event) => {
    if (otpChecking) {
      event.preventDefault();
      return;
    }
    if (event.key === "Backspace") {
      if (index > 0) {
        const next = [...otpDigits];
        if (next[index]) {
          next[index] = "";
        } else {
          next[index - 1] = "";
        }
        setOtpDigits(next);
        focusOtp(index - 1);
      } else {
        const next = [...otpDigits];
        next[0] = "";
        setOtpDigits(next);
      }
      event.preventDefault();
    }
  };

  const resendRemainingMs = Math.max(0, resendAvailableAt - Date.now());
  const resendMinutes = String(Math.floor(resendRemainingMs / 60000)).padStart(
    1,
    "0",
  );
  const resendSeconds = String(
    Math.floor((resendRemainingMs % 60000) / 1000),
  ).padStart(2, "0");
  const currentYear = new Date().getFullYear();
  const screenAnim = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
    exit: { opacity: 0, y: -15, transition: { duration: 0.2 } },
  };

  const otpFieldStyle = useMemo(
    () => ({
      ...fieldStyle,
      width: 40,
      "& .MuiOutlinedInput-root": {
        ...fieldStyle["& .MuiOutlinedInput-root"],
        px: 0,
        backgroundColor: fieldStyle["& .MuiOutlinedInput-root"].backgroundColor,
        boxShadow: otpSuccess
          ? "0 0 0 1px rgba(22,163,74,0.24)"
          : otpError
            ? "0 0 0 1px rgba(220,38,38,0.18)"
            : undefined,
      },
      "& .MuiOutlinedInput-root fieldset": otpSuccess
        ? { borderColor: "#16a34a !important" }
        : otpError
          ? { borderColor: "#dc2626 !important" }
          : otpChecking
            ? {
                borderColor: isDark
                  ? "rgba(100,116,139,0.68) !important"
                  : "rgba(75,85,99,0.45) !important",
              }
            : undefined,
    }),
    [fieldStyle, isDark, otpChecking, otpError, otpSuccess],
  );

  return (
    <ThemeProvider theme={appTheme}>
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      bgcolor="background.default"
      position="relative"
      sx={{
        color: "text.primary",
        transition: "background-color 0.3s ease",
      }}
    >
      <GlobalStyles
        styles={{
          "*": { boxSizing: "border-box" },
        }}
      />
      <Box sx={{ position: "absolute", top: 16, left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, px: 2, zIndex: 1000, pointerEvents: "none" }}>
        <AnimatePresence>
          {[
            showError && { key: "error", text: showError, color: palette.danger },
            successMsg && { key: "success", text: successMsg, color: "#16a34a" },
          ].filter(Boolean).map((toast) => (
            <Box
              key={toast.key}
              component={motion.div}
              initial={{ opacity: 0, y: -12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
              sx={{ maxWidth: 420, px: 2.5, py: 1.25, borderRadius: uiRadii.card, bgcolor: toast.color, color: "#fff", fontSize: 14, fontWeight: 500, boxShadow: palette.shadowPopover }}
            >
              {toast.text}
            </Box>
          ))}
        </AnimatePresence>
      </Box>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={controls}
        style={{ width: 320 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: uiRadii.shell,
            width: 320,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            bgcolor: "background.paper",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: palette.shadowShell,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <BrandLogo text={APP_LOGIN_LOGO_TEXT} theme={resolvedLoginTheme} fontSize={24} />
          </Box>

          <AnimatePresence mode="wait">
            {mode === "login" && (
              <motion.div key="login" layout {...screenAnim}>
                <form
                  id="tracker-auth-login-form"
                  name="tracker-auth-login-form"
                  autoComplete="on"
                  onSubmit={handleSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <TextField
                    id="tracker-auth-login-email"
                    name="tracker_login_email"
                    type="email"
                    autoComplete="section-tracker-login username"
                    label={t("common.email", activeLocale)}
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    fullWidth
                    variant="outlined"
                    inputRef={usernameRef}
                    autoFocus
                    sx={fieldStyle}
                  />
                  <TextField
                    id="tracker-auth-login-password"
                    name="tracker_login_password"
                    autoComplete="section-tracker-login current-password"
                    label={t("common.password", activeLocale)}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={fieldStyle}
                  />
                  {allowRememberMe && (
                    <Box
                      sx={{
                        mt: -0.75,
                        mb: -0.35,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={(event) =>
                              setRememberMe(event.target.checked)
                            }
                            size="small"
                            sx={{
                              p: 0.35,
                              mr: 0.7,
                              color: "text.disabled",
                              "& .MuiSvgIcon-root": { fontSize: 19 },
                              "&.Mui-checked": {
                                color: "text.secondary",
                              },
                            }}
                          />
                        }
                        label={t("login.rememberMe", activeLocale)}
                        sx={{
                          ml: 0,
                          mr: 0,
                          color: "text.secondary",
                          "& .MuiFormControlLabel-label": {
                            display: "inline-block",
                            fontSize: 13,
                            fontWeight: 400,
                            lineHeight: 1.4,
                            letterSpacing: "0.01em",
                            fontFamily: "inherit",
                          },
                        }}
                      />
                    </Box>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!username.trim() || !password.trim() || loading}
                    sx={buttonStyle}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      t("login.signIn", activeLocale)
                    )}
                  </Button>
                </form>
                <Box textAlign="center" sx={{ mt: 2.6 }}>
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => setMode("reset_email")}
                    sx={{
                      ...linkStyle,
                      p: 0,
                      m: 0,
                      mt: 0,
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      appearance: "none",
                      WebkitAppearance: "none",
                    }}
                  >
                    {t("login.forgotPassword", activeLocale)}
                  </Typography>
                </Box>
              </motion.div>
            )}

            {mode === "reset_email" && (
              <motion.div key="reset_email" layout {...screenAnim}>
                <form
                  id="tracker-password-reset-email-form"
                  name="tracker-password-reset-email-form"
                  autoComplete="off"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (resetEmail.trim()) handleSendEmail();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <TextField
                    id="tracker-password-reset-email"
                    name="tracker_reset_email"
                    type="email"
                    autoComplete="section-tracker-reset email"
                    label={t("common.email", activeLocale)}
                    fullWidth
                    value={resetEmail}
                    onChange={(event) => setResetEmail(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                    autoFocus
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!resetEmail.trim() || loading}
                    sx={buttonStyle}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      t("common.next", activeLocale)
                    )}
                  </Button>
                </form>
                <Box textAlign="center" sx={{ mt: 2.6 }}>
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => {
                      resetAllFields();
                      setMode("login");
                    }}
                    sx={{
                      ...linkStyle,
                      p: 0,
                      m: 0,
                      mt: 0,
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      appearance: "none",
                      WebkitAppearance: "none",
                    }}
                  >
                    {t("login.back", activeLocale)}
                  </Typography>
                </Box>
              </motion.div>
            )}

            {mode === "invitation" && (
              <motion.div key="invitation" layout {...screenAnim}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    mb: 3,
                  }}
                >
                  {activeLocale === "en"
                    ? "Set a password for your account"
                    : "Задайте пароль для входа"}
                  {invitationEmail ? ` ${invitationEmail}` : ""}
                </Typography>
                <form
                  id="tracker-invitation-password-form"
                  name="tracker-invitation-password-form"
                  autoComplete="off"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleAcceptInvitation();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <TextField
                    id="tracker-invitation-new-password"
                    name="tracker_invitation_new_password"
                    autoComplete="section-tracker-invitation new-password"
                    label={t("login.newPassword", activeLocale)}
                    fullWidth
                    type="password"
                    value={newPass}
                    onChange={(event) => setNewPass(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                    disabled={!invitationValid || loading}
                    autoFocus
                  />
                  <TextField
                    id="tracker-invitation-repeat-password"
                    name="tracker_invitation_repeat_password"
                    autoComplete="section-tracker-invitation new-password"
                    label={t("login.repeatPassword", activeLocale)}
                    fullWidth
                    type="password"
                    value={newPass2}
                    onChange={(event) => setNewPass2(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                    disabled={!invitationValid || loading}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={
                      !invitationValid ||
                      !newPass.trim() ||
                      !newPass2.trim() ||
                      loading
                    }
                    sx={buttonStyle}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : activeLocale === "en" ? (
                      "Set password"
                    ) : (
                      "Задать пароль"
                    )}
                  </Button>
                </form>
              </motion.div>
            )}

            {mode === "reset_code" && (
              <motion.div key="reset_code" layout {...screenAnim}>
                <form
                  id="tracker-password-reset-code-form"
                  name="tracker-password-reset-code-form"
                  autoComplete="off"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleResetPassword();
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <TextField
                    id="tracker-password-reset-code"
                    name="tracker_reset_code"
                    autoComplete="section-tracker-reset one-time-code"
                    label={t("login.resetCode", activeLocale)}
                    fullWidth
                    value={resetCode}
                    onChange={(event) => setResetCode(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                    autoFocus
                  />
                  <TextField
                    id="tracker-password-reset-new-password"
                    name="tracker_reset_new_password"
                    autoComplete="section-tracker-reset new-password"
                    label={t("login.newPassword", activeLocale)}
                    fullWidth
                    type="password"
                    value={newPass}
                    onChange={(event) => setNewPass(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                  />
                  <TextField
                    id="tracker-password-reset-repeat-password"
                    name="tracker_reset_repeat_password"
                    autoComplete="section-tracker-reset new-password"
                    label={t("login.repeatPassword", activeLocale)}
                    fullWidth
                    type="password"
                    value={newPass2}
                    onChange={(event) => setNewPass2(event.target.value)}
                    variant="outlined"
                    sx={fieldStyle}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={
                      !resetCode.trim() ||
                      !newPass.trim() ||
                      !newPass2.trim() ||
                      loading
                    }
                    sx={buttonStyle}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      t("common.confirm", activeLocale)
                    )}
                  </Button>
                </form>
                <Box textAlign="center" sx={{ mt: 2.6 }}>
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => {
                      resetAllFields();
                      setMode("reset_email");
                    }}
                    sx={{
                      ...linkStyle,
                      p: 0,
                      m: 0,
                      mt: 0,
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      appearance: "none",
                      WebkitAppearance: "none",
                    }}
                  >
                    {t("login.back", activeLocale)}
                  </Typography>
                </Box>
              </motion.div>
            )}

            {mode === "two_factor" && (
              <motion.div
                key="two_factor"
                layout
                {...screenAnim}
                style={{ width: "100%" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    width: "100%",
                    maxWidth: 248,
                    mx: "auto",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    {t("login.twoFactorPrompt", activeLocale)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.75,
                      width: "100%",
                    }}
                  >
                    {otpDigits.map((digit, index) => (
                      <TextField
                        id={`tracker-two-factor-digit-${index + 1}`}
                        name={`tracker_two_factor_digit_${index + 1}`}
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        key={index}
                        value={digit}
                        onChange={(event) =>
                          handleOtpChange(index, event.target.value)
                        }
                        onKeyDown={(event) => handleOtpKeyDown(index, event)}
                        onPaste={handleOtpPaste}
                        inputRef={(node) => {
                          otpRefs.current[index] = node;
                        }}
                        autoFocus={index === 0}
                        disabled={otpChecking}
                        inputProps={{
                          maxLength: 1,
                          inputMode: "numeric",
                          style: {
                            textAlign: "center",
                            fontSize: 22,
                            fontWeight: 600,
                            padding: "14px 0",
                          },
                        }}
                        sx={otpFieldStyle}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      minHeight: 28,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {loading && !otpSuccess && !otpError ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {t("login.verifyingCode", activeLocale)}
                        </Typography>
                      </Box>
                    ) : otpSending ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {t("login.codeSent", activeLocale)}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                  <Box
                    textAlign="center"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      component="button"
                      type="button"
                      disabled={
                        Date.now() < resendAvailableAt || otpSending || loading
                      }
                      onClick={handleResendCode}
                      sx={{
                        ...linkStyle,
                        p: 0,
                        m: 0,
                        mt: 0,
                        border: 0,
                        background: "transparent",
                        cursor:
                          Date.now() < resendAvailableAt
                            ? "default"
                            : "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                        opacity: Date.now() < resendAvailableAt ? 0.55 : 1,
                      }}
                    >
                      {Date.now() < resendAvailableAt
                        ? `${t("login.resendCodeIn", activeLocale).split("{minutes}:{seconds}")[0]}${resendMinutes}:${resendSeconds}`
                        : t("login.resendCode", activeLocale)}
                    </Typography>
                    <Typography
                      component="button"
                      type="button"
                      onClick={handleBackToLogin}
                      sx={{
                        ...linkStyle,
                        p: 0,
                        m: 0,
                        mt: 0,
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                      }}
                    >
                      {t("login.back", activeLocale)}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Paper>
      </motion.div>

      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          width: "100%",
          textAlign: "center",
          fontSize: 12,
          color: "text.secondary",
        }}
      >
        {APP_FOOTER_TEXT} © {currentYear}
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default Login;
